import { useSelector } from "react-redux";
import { GlobalState } from "@/Reducers/interface";
import { Document } from "@/Contracts/Documents";
import useDispatch from "@/Components/Utils/useDispatch";
import {
	BlockType,
	PAGE_BREAK_BLOCK_DRAGGABLE,
	PAYMENT_SUMMARY_BLOCK_DRAGGABLE
} from "@/Contracts/ProposalBuilder/Block";
import DocumentsActionCreators from "@/Actions/Documents";
import BlocksActionCreators from "@/ProposalBuilder/store/actions/Blocks";

import { ZMessage } from "@molecules";
import {
	ProposalBuilderState,
	useProposalBuilderDispatch,
	useProposalBuilderSelector
} from "@/ProposalBuilder/store";
import { useCallback } from "react";

const EMPTY_BLOCK_LIST: string[] = [];

export const usePaymentActions = () => {
	const proposalBuilderDispatch = useProposalBuilderDispatch();
	const blockList = useSelector((state: GlobalState) => {
		const document = state.documents.currentActiveDocument;
		return (
			(document && Document.getLatestRevisionBlockList(document)) ||
			EMPTY_BLOCK_LIST
		);
	});

	const { blockMapper } = useProposalBuilderSelector(
		(state: ProposalBuilderState) => ({
			blockMapper: state.blocks.blockMapper
		})
	);

	const dispatch = useDispatch();

	const removePaymentSummaryBlockFromDocument = useCallback(async () => {
		blockList.forEach(async (block, _index) => {
			if (blockMapper[block]?.type === BlockType.PAYMENT_SUMMARY) {
				await dispatch(
					DocumentsActionCreators.deleteBlockFromRevision({
						blockIds: [block]
					})
				);
				await proposalBuilderDispatch(
					BlocksActionCreators.deleteBlock({ blockIds: [block] })
				);
			}
		});
	}, [blockList, blockMapper]);

	const addPaymentSummaryBlockToDocument = async () => {
		let lastQuoteBlockIndex = -1;
		let isLastBlockPdf = false;
		let isPenUltimateBlockPdf = false;
		let isLastBlockPageBreak = false;
		blockList.forEach((block, index) => {
			if (blockMapper[block]?.type === BlockType.QUOTE) {
				lastQuoteBlockIndex = index + 1;
			} else if (index === blockList.length - 1) {
				isLastBlockPdf = blockMapper[block]?.type === BlockType.PDF;
				isLastBlockPageBreak =
					blockMapper[block]?.type === BlockType.PAGE_BREAK;
			} else if (index === blockList.length - 2) {
				isPenUltimateBlockPdf =
					blockMapper[block]?.type === BlockType.PDF;
			}
		});
		if (lastQuoteBlockIndex === -1) {
			if (isLastBlockPdf) {
				await dispatch(
					DocumentsActionCreators.addBlockToRevision({
						draggableId: PAGE_BREAK_BLOCK_DRAGGABLE,
						index: blockList.length,
						isNewQuoteBlockDesign: true
					})
				);

				await dispatch(
					DocumentsActionCreators.addBlockToRevision({
						draggableId: PAGE_BREAK_BLOCK_DRAGGABLE,
						index: blockList.length,
						isNewQuoteBlockDesign: true
					})
				);
			} else if (isPenUltimateBlockPdf && isLastBlockPageBreak) {
				await dispatch(
					DocumentsActionCreators.addBlockToRevision({
						draggableId: PAGE_BREAK_BLOCK_DRAGGABLE,
						index: blockList.length,
						isNewQuoteBlockDesign: true
					})
				);
			}
			lastQuoteBlockIndex = blockList.length - 1;
		}
		await dispatch(
			DocumentsActionCreators.addBlockToRevision({
				draggableId: PAYMENT_SUMMARY_BLOCK_DRAGGABLE,
				index: lastQuoteBlockIndex,
				isNewQuoteBlockDesign: true
			})
		);
	};

	const disablePayment = async (documentId: string) => {
		try {
			await dispatch(
				DocumentsActionCreators.updateDocumentPaymentSettings(
					documentId,
					{
						reset: true
					}
				)
			);
			await dispatch(
				DocumentsActionCreators.getDocumentDetails({
					documentId
				})
			);
			ZMessage.success("Payment Disabled");
		} catch (error) {
			console.error(error);
		}
	};

	return {
		removePaymentSummaryBlockFromDocument,
		addPaymentSummaryBlockToDocument,
		disablePayment
	};
};
